import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../Components/Seo";
import SectionHeading from "../Components/SectionHeading";
import ServiceCard from "../Components/ServiceCard";
import StatCounter from "../Components/StatCounter";
import CTASection from "../Components/CTASection";
import { getIcon } from "../lib/icons";
import { SERVICES } from "../data/services";
import {
  HERO_HIGHLIGHTS,
  INDUSTRIES,
  PROCESS,
  SITE,
  STATS,
  TECH_PARTNERS,
  TECH_STACK,
  WHY_CHOOSE,
} from "../data/site";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const HomePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.name,
      url: SITE.domain,
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: SITE.name,
      image: `${SITE.domain}/logo512.png`,
      email: SITE.email,
      url: SITE.domain,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Bartlesville",
        addressRegion: "OK",
        addressCountry: "US",
      },
      areaServed: "United States",
      description: SITE.description,
    },
  ];

  return (
    <>
      <Seo
        title="Triguna Solutions | AI, Software Development, Cloud & Data Engineering Services"
        description={SITE.description}
        path="/"
        jsonLd={jsonLd}
      />

      {/* ===== HERO (dark, enterprise) ===== */}
      <section className="relative overflow-hidden bg-brand-gradient-dark">
        {/* ambient glow + grid */}
        <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="pointer-events-none absolute right-0 top-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_55%)]" />

        <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8 lg:pb-28 lg:pt-36">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/90 backdrop-blur">
                AI-First Technology Partner
              </span>
              <h1 className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Transform Ideas Into{" "}
                <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  Intelligent Digital Solutions
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-purple-100/80">
                Accelerate innovation with Generative AI, custom software, cloud
                engineering, and data-driven solutions built for growth.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => navigate("/contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/40"
                >
                  Schedule Consultation
                  <ArrowRight className="h-5 w-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => navigate("/services")}
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
                >
                  Explore Services
                </motion.button>
              </div>

              <ul className="mt-10 grid grid-cols-2 gap-3">
                {HERO_HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white/80">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success/20 text-success">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
                <img
                  src="https://images.unsplash.com/photo-1531973576160-7125cd663d86?auto=format&fit=crop&w=1100&q=80"
                  alt="Triguna Solutions engineering team collaborating on AI and software projects"
                  loading="eager"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-border bg-card p-5 shadow-xl sm:block">
                <p className="font-display text-3xl font-bold text-foreground">
                  <StatCounter value={98} suffix="%" />
                </p>
                <p className="text-sm text-muted-foreground">Client Satisfaction</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PARTNER LOGO STRIP ===== */}
      <section className="border-b border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Powered by industry-leading technologies
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {TECH_PARTNERS.map((partner) => (
              <span
                key={partner}
                className="font-display text-lg font-semibold text-foreground/40 transition-colors hover:text-foreground/70"
              >
                {partner}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-4 lg:px-8">
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-display text-4xl font-bold text-foreground sm:text-5xl">
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  <StatCounter value={stat.value} suffix={stat.suffix} />
                </span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TRUSTED PARTNER ===== */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-primary">
            Your Trusted Technology Partner
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Helping Businesses Build, Scale, and Innovate
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            Triguna Solutions empowers organizations with cutting-edge
            technologies, combining AI expertise, cloud-native architectures, and
            engineering excellence to deliver measurable business outcomes.
          </p>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="What We Do"
            title="Technology Services Designed for Modern Businesses"
            subtitle="End-to-end engineering across AI, software, cloud, and data — tailored to your goals."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, i) => (
              <ServiceCard key={service.slug} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE ===== */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Why Triguna"
            title="Why Businesses Choose Triguna Solutions"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((item, i) => (
              <motion.div
                key={item.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Check className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES ===== */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Industries"
            title="Industries We Serve"
            subtitle="Deep domain expertise across the sectors driving the digital economy."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {INDUSTRIES.map((industry, i) => {
              const Icon = getIcon(industry.icon);
              return (
                <motion.div
                  key={industry.name}
                  {...fadeUp}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.08 }}
                  className="group rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gradient text-white">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-semibold text-foreground">
                    {industry.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{industry.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TECH STACK ===== */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Technology Stack"
            title="Modern Tools. Proven Platforms."
            subtitle="We work across the technologies that power today's most ambitious products."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TECH_STACK.map((group, i) => (
              <motion.div
                key={group.category}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <h3 className="font-display text-base font-semibold text-primary">
                  {group.category}
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {group.items.map((tech) => (
                    <span
                      key={tech}
                      className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground/80"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="How We Work"
            title="A Proven Delivery Process"
            subtitle="A clear, collaborative path from idea to impact."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((item, i) => (
              <motion.div
                key={item.step}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                className="relative rounded-2xl border border-border bg-card p-6"
              >
                <span className="font-display text-3xl font-bold text-primary/20">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-display text-lg font-semibold text-foreground">
                  {item.step}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <CTASection />
    </>
  );
};

export default HomePage;
